// Wizard setup 2FA TOTP (Sprint 5).
// Tre step: init (mostra QR + recovery codes) → verify (codice 6-digit) → done.

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { api, ApiClientError } from '../../lib/api';

type Stage = 'intro' | 'show-secret' | 'verify' | 'done';

export function Setup2faPage({ forceMode = false }: { forceMode?: boolean }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [stage, setStage] = useState<Stage>('intro');
  const [secret, setSecret] = useState<string>('');
  const [otpauthUri, setOtpauthUri] = useState<string>('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [savedRecovery, setSavedRecovery] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const initMutation = useMutation({
    mutationFn: () => api.totpSetupInit(),
    onSuccess: async (data) => {
      setSecret(data.secret);
      setOtpauthUri(data.otpauth_uri);
      setRecoveryCodes(data.recovery_codes);
      const qr = await QRCode.toDataURL(data.otpauth_uri, { width: 220, margin: 1 });
      setQrDataUrl(qr);
      setStage('show-secret');
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (code: string) => api.totpSetupVerify(code),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['totp', 'status'] });
      setStage('done');
    },
    onError: (err) => {
      if (err instanceof ApiClientError) {
        setVerifyError(err.payload.error);
      } else {
        setVerifyError('Errore di rete');
      }
    },
  });

  function copyAllRecovery() {
    navigator.clipboard.writeText(recoveryCodes.join('\n')).catch(() => {});
  }

  function downloadRecoveryFile() {
    const content = `# Audiq · Codici di recupero 2FA\n# Salvali in un posto sicuro. Ogni codice è monouso.\n\n${recoveryCodes.join('\n')}\n`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'audiq-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div
      className="rounded-lg border p-6"
      style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
    >
      <div className="mb-4 mono text-xs" style={{ color: 'var(--mute)' }}>
        Sicurezza personale · 2FA
      </div>

      {stage === 'intro' && (
        <div>
          <h2 className="serif mb-3 text-2xl" style={{ color: 'var(--ink)' }}>
            {forceMode ? 'Configura 2FA per continuare' : 'Configura 2FA'}
          </h2>
          <p className="mb-4 text-sm" style={{ color: 'var(--ink)' }}>
            La verifica in due passaggi protegge il tuo account anche se qualcuno scopre la tua
            password. Ti servirà un'app authenticator (Google Authenticator, Authy, 1Password, …).
          </p>
          {forceMode && (
            <div
              className="mb-4 rounded-md border-l-4 p-3 text-sm"
              style={{
                borderColor: 'var(--accent)',
                background: 'color-mix(in oklab, var(--accent) 12%, transparent)',
                color: 'var(--ink)',
              }}
            >
              Il periodo di grazia è scaduto: come admin master, devi configurare il 2FA prima di
              accedere alla console.
            </div>
          )}
          <ol className="mb-6 list-decimal space-y-1 ps-6 text-sm" style={{ color: 'var(--ink)' }}>
            <li>Apri la tua app authenticator preferita</li>
            <li>Scansiona il QR code che mostriamo al passo successivo</li>
            <li>Inserisci il codice a 6 cifre per confermare</li>
            <li>Salva i codici di recovery (non te li potremo più mostrare)</li>
          </ol>
          <button
            onClick={() => initMutation.mutate()}
            disabled={initMutation.isPending}
            className="rounded-md px-4 py-2 text-sm font-medium shadow-sm transition disabled:opacity-60"
            style={{ background: 'var(--primary)', color: '#fff' }}
          >
            {initMutation.isPending ? 'Genero…' : 'Inizia setup'}
          </button>
          {initMutation.isError && (
            <p className="mt-3 text-sm" style={{ color: 'var(--danger)' }}>
              {initMutation.error instanceof ApiClientError
                ? initMutation.error.payload.error
                : 'Errore'}
            </p>
          )}
        </div>
      )}

      {stage === 'show-secret' && (
        <div>
          <h2 className="serif mb-3 text-2xl" style={{ color: 'var(--ink)' }}>
            Scansiona il QR code
          </h2>

          <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-[auto_1fr]">
            <div className="flex flex-col items-center gap-2">
              {qrDataUrl && (
                <img
                  src={qrDataUrl}
                  alt="QR code 2FA"
                  className="rounded-md border p-2"
                  style={{ background: '#fff', borderColor: 'var(--line)' }}
                />
              )}
              <div className="text-center mono text-xs" style={{ color: 'var(--mute)' }}>
                Oppure inserisci manualmente
              </div>
              <code
                className="mono break-all rounded-md border p-2 text-xs"
                style={{ borderColor: 'var(--line)', background: 'var(--soft)', maxWidth: 220 }}
              >
                {secret}
              </code>
            </div>

            <div>
              <h3 className="mb-2 text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                Codici di recupero
              </h3>
              <p className="mb-3 text-xs" style={{ color: 'var(--mute)' }}>
                Salvali ora. Servono se perdi l'accesso all'app authenticator. Ogni codice è
                monouso.
              </p>
              <div
                className="grid grid-cols-2 gap-1 rounded-md border p-3 mono text-sm"
                style={{ borderColor: 'var(--line)', background: 'var(--soft)' }}
              >
                {recoveryCodes.map((code) => (
                  <div key={code}>{code}</div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={copyAllRecovery}
                  className="rounded-md border px-3 py-1.5 text-xs"
                  style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
                >
                  Copia tutti
                </button>
                <button
                  onClick={downloadRecoveryFile}
                  className="rounded-md border px-3 py-1.5 text-xs"
                  style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
                >
                  Scarica .txt
                </button>
              </div>
            </div>
          </div>

          <label className="mb-4 flex items-start gap-2 text-sm" style={{ color: 'var(--ink)' }}>
            <input
              type="checkbox"
              checked={savedRecovery}
              onChange={(e) => setSavedRecovery(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Confermo di aver salvato i codici di recupero in un posto sicuro. Capisco che non
              potrò più visualizzarli.
            </span>
          </label>

          <button
            onClick={() => setStage('verify')}
            disabled={!savedRecovery}
            className="rounded-md px-4 py-2 text-sm font-medium shadow-sm transition disabled:opacity-50"
            style={{ background: 'var(--primary)', color: '#fff' }}
          >
            Continua →
          </button>
        </div>
      )}

      {stage === 'verify' && (
        <div>
          <h2 className="serif mb-3 text-2xl" style={{ color: 'var(--ink)' }}>
            Verifica il codice
          </h2>
          <p className="mb-4 text-sm" style={{ color: 'var(--ink)' }}>
            Inserisci il codice a 6 cifre che vedi nell'app authenticator.
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            value={verifyCode}
            onChange={(e) => {
              setVerifyCode(e.target.value.replace(/\D/g, ''));
              setVerifyError(null);
            }}
            className="mono mb-3 w-48 rounded-md border px-3 py-2 text-center text-xl tracking-widest shadow-sm focus:outline-none focus:ring-2"
            style={{
              background: 'var(--paper)',
              borderColor: 'var(--line)',
              color: 'var(--ink)',
              /* @ts-expect-error */
              '--tw-ring-color': 'var(--primary)',
              letterSpacing: '0.3em',
            }}
            placeholder="000000"
          />
          {verifyError && (
            <p className="mb-3 text-sm" style={{ color: 'var(--danger)' }}>
              {verifyError}
            </p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => verifyMutation.mutate(verifyCode)}
              disabled={verifyCode.length !== 6 || verifyMutation.isPending}
              className="rounded-md px-4 py-2 text-sm font-medium shadow-sm transition disabled:opacity-50"
              style={{ background: 'var(--primary)', color: '#fff' }}
            >
              {verifyMutation.isPending ? 'Verifica…' : 'Conferma e attiva'}
            </button>
            <button
              onClick={() => setStage('show-secret')}
              className="rounded-md border px-4 py-2 text-sm"
              style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
            >
              Torna indietro
            </button>
          </div>
        </div>
      )}

      {stage === 'done' && (
        <div>
          <h2 className="serif mb-3 text-2xl" style={{ color: 'var(--primary)' }}>
            ✓ 2FA attivo
          </h2>
          <p className="mb-4 text-sm" style={{ color: 'var(--ink)' }}>
            Da ora in poi ti chiederemo il codice a 6 cifre dopo email e password ad ogni login.
          </p>
          <button
            onClick={() => navigate(forceMode ? '/' : '/settings/security', { replace: true })}
            className="rounded-md px-4 py-2 text-sm font-medium shadow-sm"
            style={{ background: 'var(--primary)', color: '#fff' }}
          >
            {forceMode ? 'Vai alla dashboard' : 'Torna alla sicurezza'}
          </button>
        </div>
      )}
    </div>
  );
}
